/**
 * Iranian identifier validation.
 *
 * Single source of truth for the browser form (`client/src/lib/form-utils.ts`) and the gateway
 * request contracts (`server/http/schemas.ts`) so the two can never drift apart.
 * Pure, dependency-free, no DOM.
 *
 * Sources for each rule are cited on the function that implements it.
 */

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const ARABIC_INDIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';

/**
 * Code points a pasted identifier commonly carries that never change its
 * meaning: the ZWNJ/ZWJ and bidi marks Persian text drags along, plus the BOM.
 * Listed numerically because they are invisible in source.
 */
const IGNORED_CODE_POINTS = new Set([
  0x200b, // zero-width space
  0x200c, // zero-width non-joiner
  0x200d, // zero-width joiner
  0x200e, // left-to-right mark
  0x200f, // right-to-left mark
  0x2066, // left-to-right isolate
  0x2067, // right-to-left isolate
  0x2068, // first-strong isolate
  0x2069, // pop directional isolate
  0xfeff, // byte-order mark
]);

/** Separators people type or paste into a long number. */
const IGNORED_SEPARATORS = /[\s._/\\-]/;

/** Normalize numerals from either Persian keyboard layout to API-safe ASCII. */
export function toEnglishDigits(value: string): string {
  return [...value].map((character) => {
    const persianIndex = PERSIAN_DIGITS.indexOf(character);
    if (persianIndex >= 0) return String(persianIndex);
    const arabicIndex = ARABIC_INDIC_DIGITS.indexOf(character);
    return arabicIndex >= 0 ? String(arabicIndex) : character;
  }).join('');
}

/** ASCII digits with the separators people paste out of PDFs and spreadsheets removed. */
export function normalizeIdentifier(value: string): string {
  return [...toEnglishDigits(String(value ?? ''))]
    .filter((character) => !IGNORED_CODE_POINTS.has(character.codePointAt(0) ?? 0)
      && !IGNORED_SEPARATORS.test(character))
    .join('');
}

export type IdentifierIssue =
  | 'not_numeric'
  /** Right shape, wrong number of digits. */
  | 'length'
  /** 0000000000, 1111111111, … — structurally well-formed placeholders. */
  | 'repeated_digit'
  /** The unique-serial block is all zeros, so the value identifies nobody. */
  | 'blank_serial'
  /** Failed the published mod-11 check digit. */
  | 'checksum';

export interface IdentifierResult {
  ok: boolean;
  issue?: IdentifierIssue;
}

const OK: IdentifierResult = { ok: true };
const fail = (issue: IdentifierIssue): IdentifierResult => ({ ok: false, issue });

function isAllDigits(value: string): boolean {
  return /^\d+$/.test(value);
}

function isRepeatedDigit(value: string): boolean {
  return /^(\d)\1*$/.test(value);
}

/* -------------------------------------------------------------------------- */
/* کد ملی — national code of a natural person (10 digits)                      */
/* -------------------------------------------------------------------------- */

/**
 * Check digit for a 10-digit کد ملی: weight the first 9 digits by 10…2,
 * take mod 11, and the remainder is the check digit — except that a remainder
 * of 2 or more is subtracted from 11.
 */
export function nationalIdCheckDigit(firstNineDigits: string): number {
  let sum = 0;
  for (let index = 0; index < 9; index += 1) {
    sum += Number(firstNineDigits[index]) * (10 - index);
  }
  const remainder = sum % 11;
  return remainder < 2 ? remainder : 11 - remainder;
}

/**
 * Validate a 10-digit Iranian کد ملی.
 *
 * The three-digit issuing-city prefix is deliberately NOT checked against a
 * fixed allow-list: those lists go stale as new prefixes are issued, and a
 * financial record is worse off rejecting a real customer than accepting a
 * mistyped-but-checksum-valid one. The mod-11 digit is the definitive test.
 */
export function checkNationalId(rawValue: string): IdentifierResult {
  const value = normalizeIdentifier(rawValue);
  if (!isAllDigits(value)) return fail('not_numeric');
  if (value.length !== 10) return fail('length');
  if (isRepeatedDigit(value)) return fail('repeated_digit');
  // Digits 4..9 are the person-unique block; all-zero there identifies nobody.
  if (Number(value.slice(3, 9)) === 0) return fail('blank_serial');
  return Number(value[9]) === nationalIdCheckDigit(value) ? OK : fail('checksum');
}

export function isNationalId(rawValue: string): boolean {
  return checkNationalId(rawValue).ok;
}

/* -------------------------------------------------------------------------- */
/* شناسه ملی — national ID of a legal entity (11 digits)                       */
/* -------------------------------------------------------------------------- */

const LEGAL_ID_COEFFICIENTS = [29, 27, 23, 19, 17];

/**
 * Check digit for an 11-digit شناسه ملی.
 *
 * Take the 10th digit plus 2, add that constant to each of the first ten
 * digits, multiply by the repeating coefficient series 29/27/23/19/17, sum,
 * and take mod 11 — a remainder of 10 collapses to 0.
 *
 * Worked reference: 10380284790 → weighted sum 3432 → 3432 mod 11 = 0 → check digit 0.
 */
export function legalIdCheckDigit(firstTenDigits: string): number {
  const offset = Number(firstTenDigits[9]) + 2;
  let sum = 0;
  for (let index = 0; index < 10; index += 1) {
    sum += (offset + Number(firstTenDigits[index])) * LEGAL_ID_COEFFICIENTS[index % 5];
  }
  const remainder = sum % 11;
  return remainder === 10 ? 0 : remainder;
}

/**
 * Validate an 11-digit شناسه ملی: four digits of registration-place code, six
 * digits unique to the entity, one check digit.
 */
export function checkLegalId(rawValue: string): IdentifierResult {
  const value = normalizeIdentifier(rawValue);
  if (!isAllDigits(value)) return fail('not_numeric');
  if (value.length !== 11) return fail('length');
  if (isRepeatedDigit(value)) return fail('repeated_digit');
  if (Number(value.slice(3, 9)) === 0) return fail('blank_serial');
  return Number(value[10]) === legalIdCheckDigit(value.slice(0, 10)) ? OK : fail('checksum');
}

export function isLegalId(rawValue: string): boolean {
  return checkLegalId(rawValue).ok;
}

/* -------------------------------------------------------------------------- */
/* کد اقتصادی — economic (tax) code                                            */
/* -------------------------------------------------------------------------- */

export type EconomicCodeKind =
  /** 11 digits, identical to the entity's شناسه ملی. The modern default for a company. */
  | 'legal-entity'
  /** 11 digits beginning with 6 — a civil partnership (مشارکت مدنی). */
  | 'civil-partnership'
  /** 14 digits whose leading 10 are a valid کد ملی: a sole proprietor plus file number. */
  | 'natural-person'
  /** 14 digits that do not open with a کد ملی — foreign nationals, or a branch file. */
  | 'foreign-or-branch'
  /** The withdrawn 12-digit code. Still present on historical documents. */
  | 'legacy';

export interface EconomicCodeResult extends IdentifierResult {
  kind?: EconomicCodeKind;
}

/**
 * Validate a کد اقتصادی across every form still in circulation.
 *
 * Under the current tax regime a legal entity's economic code simply *is* its
 * 11-digit شناسه ملی, so an 11-digit value is held to the شناسه ملی check digit.
 * Civil partnerships also use 11 digits but conventionally begin with 6 and have
 * no published check digit, so they are accepted structurally.
 *
 * Sole proprietors get 14 digits: their 10-digit کد ملی followed by a four-digit
 * file number issued by the tax administration. Foreign nationals also hold
 * 14-digit codes built on the 12-digit کد فراگیر instead, so a 14-digit value
 * that does not open with a valid کد ملی is classified rather than rejected.
 *
 * The 12-digit code (the "411…" generation) is withdrawn but still appears on
 * archived invoices, so it is accepted and reported as legacy.
 */
export function checkEconomicCode(rawValue: string): EconomicCodeResult {
  const value = normalizeIdentifier(rawValue);
  if (!isAllDigits(value)) return fail('not_numeric');
  if (value.length !== 11 && value.length !== 12 && value.length !== 14) return fail('length');
  if (isRepeatedDigit(value)) return fail('repeated_digit');

  if (value.length === 11) {
    if (value.startsWith('6')) return { ok: true, kind: 'civil-partnership' };
    const legal = checkLegalId(value);
    return legal.ok ? { ok: true, kind: 'legal-entity' } : legal;
  }

  // No internal layout is published for the withdrawn 12-digit code, so beyond
  // "twelve digits, not a single repeated one" there is nothing to check.
  if (value.length === 12) return { ok: true, kind: 'legacy' };

  return {
    ok: true,
    kind: isNationalId(value.slice(0, 10)) ? 'natural-person' : 'foreign-or-branch',
  };
}

export function isEconomicCode(rawValue: string): boolean {
  return checkEconomicCode(rawValue).ok;
}

/* -------------------------------------------------------------------------- */
/* شماره ثبت — company registration number                                     */
/* -------------------------------------------------------------------------- */

/** Registration numbers run to six digits in practice; allow headroom, not 30. */
export const MAX_REGISTRATION_NUMBER_LENGTH = 10;

/**
 * Validate a شماره ثبت. It is issued per registration office, carries no check
 * digit and is not nationally unique, so only its shape can be verified.
 */
export function checkRegistrationNumber(rawValue: string): IdentifierResult {
  const value = normalizeIdentifier(rawValue);
  if (!isAllDigits(value)) return fail('not_numeric');
  if (value.length > MAX_REGISTRATION_NUMBER_LENGTH) return fail('length');
  if (Number(value) === 0) return fail('blank_serial');
  return OK;
}

export function isRegistrationNumber(rawValue: string): boolean {
  return checkRegistrationNumber(rawValue).ok;
}
